import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BasePage from './BasePage';
import { Typography, Steps, Tabs, message, Form, Input, Select, Button, Row, Col, Modal, Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { sitesService, equipamentosService, empresaClientesService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import StepSite from '../components/Cadastro/StepSite';
import StepUsinaSolar from '../components/Cadastro/StepUsinaSolar';
import StepUsinaEolica from '../components/Cadastro/StepUsinaEolica';
import StepUsinaTermeletrica from '../components/Cadastro/StepUsinaTermeletrica';
import StepBateria from '../components/Cadastro/StepBateria';
import StepGrid from '../components/Cadastro/StepGrid';
import StepCarregador from '../components/Cadastro/StepCarregador';
import MapButton from '../components/MapComponent/MapButton';
import SatelliteMap from '../components/MapComponent/SatelliteMap';

const { Title } = Typography;

const CadastrosPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [sitesExistentes, setSitesExistentes] = useState([]);
    const [selectedSite, setSelectedSite] = useState('');
    const [siteData, setSiteData] = useState(null);
    const [isSiteSaved, setIsSiteSaved] = useState(false);
    const [cepData, setCepData] = useState({ rua: '', bairro: '', cidade: '', numero: '' });
    const [logradouroNumero, setLogradouroNumero] = useState('');
    const [geocodedCoords, setGeocodedCoords] = useState(null);
    const [empresas, setEmpresas] = useState([]);
    const [activeTab, setActiveTab] = useState('cadastro');
    const [fromAneelData, setFromAneelData] = useState(null);
    const geocodeTimeoutRef = useRef(null);

    const token = localStorage.getItem('token');
    const empresaClienteId = user?.empresaClienteId;

    const steps = [
        { title: 'Site e Endereço', key: 'site' },
        { title: 'Usina Solar', key: 'solar' },
        { title: 'Usina Eólica', key: 'eolica' },
        { title: 'Usina Termelétrica', key: 'termeletrica' },
        { title: 'Bateria', key: 'bateria' },
        { title: 'Grid', key: 'grid' },
        { title: 'Carregador', key: 'carregador' },
    ];

    useEffect(() => {
        fetchSites();
        fetchEmpresas();
    }, []);

    // Preencher formulário com dados ANEEL quando vier da tabela ANEEL
    useEffect(() => {
        const aneelRecord = location.state?.fromAneel || (() => {
            try {
                const stored = sessionStorage.getItem('aneelRecordForCadastro');
                return stored ? JSON.parse(stored) : null;
            } catch {
                return null;
            }
        })();
        if (aneelRecord) {
            const lat = aneelRecord.NumCoordNEmpreendimento != null
                ? parseFloat(String(aneelRecord.NumCoordNEmpreendimento).replace(',', '.'))
                : null;
            const lng = aneelRecord.NumCoordEEmpreendimento != null
                ? parseFloat(String(aneelRecord.NumCoordEEmpreendimento).replace(',', '.'))
                : null;
            form.setFieldsValue({
                nome: aneelRecord.NomEmpreendimento || '',
                cnpjResponsavel: aneelRecord.NumCPFCNPJ || '',
                pessoaResponsavel: aneelRecord.NomAgente || '',
                latitude: lat != null && !isNaN(lat) ? lat : undefined,
                longitude: lng != null && !isNaN(lng) ? lng : undefined,
                status: aneelRecord.DscFaseUsina === 'Operação' ? 'Conectado' : 'Desconectado',
            });
            setFromAneelData(aneelRecord.NomEmpreendimento || 'Registro ANEEL');
            sessionStorage.removeItem('aneelRecordForCadastro');
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSites = async () => {
        try {
            const response = await sitesService.getAll(empresaClienteId);
            if (response?.success && response?.data) {
                setSitesExistentes(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar sites:', error);
        }
    };

    const fetchEmpresas = async () => {
        try {
            const response = await empresaClientesService.getAll();
            if (response?.success && response?.data) {
                setEmpresas(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar empresas:', error);
        }
    };

    const handleCepChange = async (value) => {
        const cep = (value || '').replace(/\D/g, '').trim();
        if (cep.length >= 8) {
            try {
                const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
                if (response.data && !response.data.erro) {
                    form.setFieldsValue({
                        rua: response.data.logradouro,
                        bairro: response.data.bairro,
                        cidade: response.data.localidade,
                        estado: response.data.uf,
                    });
                    setCepData({
                        rua: response.data.logradouro,
                        bairro: response.data.bairro,
                        cidade: response.data.localidade,
                        numero: logradouroNumero,
                    });
                    message.success('Endereço encontrado!');
                }
            } catch (error) {
                message.error('CEP não encontrado.');
            }
        }
    };

    const handleNumeroChange = (value) => {
        setLogradouroNumero(value);
        setCepData((prev) => ({ ...prev, numero: value }));
    };

    const handleSiteChange = (value) => {
        setSelectedSite(value || '');
        if (value) {
            const site = sitesExistentes.find((s) => s.id === value);
            if (site) {
                setSiteData(site);
                setIsSiteSaved(true);
                form.setFieldsValue({
                    nome: site.name || site.nome,
                    cep: site.cep,
                    rua: site.logradouro,
                    bairro: site.bairro,
                    cidade: site.city || site.cidade,
                    estado: site.state || site.estado,
                    numero: site.numero,
                    status: ['ATIVO', 'active'].includes(site.status) ? 'Conectado' : ['INATIVO', 'inactive'].includes(site.status) ? 'Desconectado' : (site.status || 'Conectado'),
                    pessoaResponsavel: site.pessoaResponsavel,
                    emailResponsavel: site.emailResponsavel,
                    cnpjResponsavel: site.cnpjResponsavel,
                    latitude: site.latitude,
                    longitude: site.longitude,
                });
                setCepData({
                    rua: site.logradouro || '',
                    bairro: site.bairro || '',
                    cidade: site.city || site.cidade || '',
                    numero: site.numero || '',
                });
                setGeocodedCoords(null);
            }
        } else {
            setSiteData(null);
            setIsSiteSaved(false);
            setGeocodedCoords(null);
            form.resetFields();
        }
    };

    const formValues = Form.useWatch([], form) || {};

    // Geocoding via Nominatim (OpenStreetMap) - gratuito, sem API key
    useEffect(() => {
        const lat = formValues?.latitude ? parseFloat(formValues.latitude) : null;
        const lng = formValues?.longitude ? parseFloat(formValues.longitude) : null;
        if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
            setGeocodedCoords(null);
            if (geocodeTimeoutRef.current) {
                clearTimeout(geocodeTimeoutRef.current);
                geocodeTimeoutRef.current = null;
            }
            return;
        }
        const rua     = formValues?.rua?.trim();
        const numero  = formValues?.numero?.toString().trim();
        const bairro  = formValues?.bairro?.trim();
        const cidade  = formValues?.cidade?.trim();
        const estado  = formValues?.estado?.trim();

        if (!rua || !cidade) {
            setGeocodedCoords(null);
            return;
        }

        if (geocodeTimeoutRef.current) clearTimeout(geocodeTimeoutRef.current);
        geocodeTimeoutRef.current = setTimeout(async () => {
            try {
                let geocodeUrl;
                if (numero) {
                    // Com número: usa busca estruturada do Nominatim para maior precisão
                    const params = new URLSearchParams({
                        format: 'json',
                        limit: '1',
                        street: `${numero} ${rua}`,
                        city: cidade,
                        state: estado || '',
                        country: 'Brasil',
                        addressdetails: '1',
                    });
                    geocodeUrl = `https://nominatim.openstreetmap.org/search?${params}`;
                } else {
                    // Sem número: busca livre pelo logradouro + bairro + cidade
                    const parts = [rua, bairro, cidade, estado].filter(Boolean);
                    geocodeUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(parts.join(', ') + ', Brasil')}&format=json&limit=1`;
                }
                const res = await fetch(geocodeUrl, {
                    headers: { 'User-Agent': 'PIADSE-Heliosfera-SmartGrid/1.0' }
                });
                const data = await res.json();
                if (data?.[0]) {
                    setGeocodedCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                } else if (numero) {
                    // Fallback: tenta novamente sem o número se não achou
                    const fallbackParts = [rua, bairro, cidade, estado].filter(Boolean);
                    const fallbackRes = await fetch(
                        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fallbackParts.join(', ') + ', Brasil')}&format=json&limit=1`,
                        { headers: { 'User-Agent': 'PIADSE-Heliosfera-SmartGrid/1.0' } }
                    );
                    const fallbackData = await fallbackRes.json();
                    setGeocodedCoords(fallbackData?.[0]
                        ? { lat: parseFloat(fallbackData[0].lat), lng: parseFloat(fallbackData[0].lon) }
                        : null
                    );
                } else {
                    setGeocodedCoords(null);
                }
            } catch {
                setGeocodedCoords(null);
            }
            geocodeTimeoutRef.current = null;
        }, 700);
        return () => {
            if (geocodeTimeoutRef.current) {
                clearTimeout(geocodeTimeoutRef.current);
                geocodeTimeoutRef.current = null;
            }
        };
    }, [formValues?.rua, formValues?.numero, formValues?.bairro, formValues?.cidade, formValues?.estado, formValues?.latitude, formValues?.longitude]);

    const mapCoords = useMemo(() => {
        const lat = formValues?.latitude ? parseFloat(formValues.latitude) : null;
        const lng = formValues?.longitude ? parseFloat(formValues.longitude) : null;
        if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
            return { lat, lng };
        }
        if (geocodedCoords) {
            return geocodedCoords;
        }
        return null;
    }, [formValues, geocodedCoords]);

    const handleSiteSave = (data) => {
        setSiteData(data);
        setIsSiteSaved(true);
        setGeocodedCoords(null);
    };

    const canProceedFromStep0 = () => isSiteSaved || !!selectedSite;

    const handleNext = () => {
        if (currentStep === 0 && !canProceedFromStep0()) {
            message.warning('Salve ou selecione um site antes de continuar.');
            return;
        }
        setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const stepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Row gutter={24} style={{ minHeight: 480 }}>
                        <Col xs={24} md={10}>
                            <StepSite
                                form={form}
                                sitesExistentes={sitesExistentes}
                                selectedSite={selectedSite}
                                onSiteChange={handleSiteChange}
                                onCepChange={handleCepChange}
                                onNumeroChange={handleNumeroChange}
                                currentStep={currentStep}
                                totalSteps={steps.length}
                                onPrev={handlePrev}
                                onNext={handleNext}
                                onSave={handleSiteSave}
                                empresaClienteId={empresaClienteId}
                            />
                            <div style={{ marginTop: 16 }}>
                                <MapButton
                                    rua={formValues?.rua || cepData.rua || 'Geolocalização'}
                                    bairro={formValues?.bairro ?? cepData.bairro}
                                    cidade={formValues?.cidade ?? cepData.cidade}
                                    displayOnly
                                    style={{ minHeight: 80 }}
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={14}>
                            <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #e8e8e8', height: '100%', minHeight: 420 }}>
                                {mapCoords ? (
                                    <SatelliteMap key={`${mapCoords.lat}-${mapCoords.lng}`} lat={mapCoords.lat} lng={mapCoords.lng} height={420} />
                                ) : (
                                    <div style={{ padding: 40, textAlign: 'center', color: '#666', minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                        Preencha o endereço (rua, número, bairro, cidade) ou latitude/longitude para visualizar no mapa.
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                );
            case 1:
                return <StepUsinaSolar siteId={siteData?.id || selectedSite} currentStep={currentStep} totalSteps={steps.length} onPrev={handlePrev} onNext={handleNext} />;
            case 2:
                return <StepUsinaEolica siteId={siteData?.id || selectedSite} currentStep={currentStep} totalSteps={steps.length} onPrev={handlePrev} onNext={handleNext} />;
            case 3:
                return <StepUsinaTermeletrica siteId={siteData?.id || selectedSite} currentStep={currentStep} totalSteps={steps.length} onPrev={handlePrev} onNext={handleNext} />;
            case 4:
                return <StepBateria siteId={siteData?.id || selectedSite} currentStep={currentStep} totalSteps={steps.length} onPrev={handlePrev} onNext={handleNext} />;
            case 5:
                return <StepGrid siteId={siteData?.id || selectedSite} currentStep={currentStep} totalSteps={steps.length} onPrev={handlePrev} onNext={handleNext} />;
            case 6:
                return <StepCarregador siteId={siteData?.id || selectedSite} currentStep={currentStep} totalSteps={steps.length} onPrev={handlePrev} onNext={handleNext} />;
            default:
                return null;
        }
    };

    return (
        <BasePage>
            <div style={{ backgroundColor: '#FFF', padding: '24px', borderRadius: '8px' }}>
                <Title level={2} style={{ marginBottom: 24 }}>
                    Cadastro de Sites e Equipamentos
                </Title>

                {fromAneelData && (
                    <Alert
                        message="Dados da ANEEL carregados"
                        description={`O formulário foi preenchido com dados de "${fromAneelData}". Revise os campos (nome, coordenadas, responsável) e complete o cadastro.`}
                        type="info"
                        showIcon
                        closable
                        onClose={() => setFromAneelData(null)}
                        style={{ marginBottom: 20 }}
                    />
                )}

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'cadastro',
                            label: 'Cadastro (Site → Geradores → Equipamentos)',
                            children: (
                                <>
                                    <p style={{ color: '#666', marginBottom: 20 }}>
                                        Selecione o tipo de dado a ser cadastrado. Formato original do projeto.
                                    </p>
                                    <Steps
                                        current={currentStep}
                                        onChange={setCurrentStep}
                                        style={{ marginBottom: 24 }}
                                        items={steps.map((s) => ({ key: s.key, title: s.title }))}
                                    />

                                    <div style={{ backgroundColor: '#fafafa', padding: 24, borderRadius: 8, marginBottom: 20 }}>
                                        {stepContent()}
                                    </div>
                                </>
                            ),
                        },
                        {
                            key: 'equipamentos',
                            label: 'Gerenciar Equipamentos',
                            children: <EquipamentosTab />,
                        },
                    ]}
                />
            </div>

        </BasePage>
    );
};

const EquipamentosTab = () => {
    const [equipments, setEquipments] = useState([]);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [eqRes, sitesRes] = await Promise.all([
                equipamentosService.getAll(),
                sitesService.getAll(),
            ]);
            setEquipments(eqRes?.data ?? eqRes ?? []);
            setSites(sitesRes?.data ?? sitesRes ?? []);
        } catch (error) {
            message.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingEquipment) {
                await equipamentosService.update(editingEquipment.id, values);
                message.success('Equipamento atualizado');
            } else {
                await equipamentosService.create(values);
                message.success('Equipamento criado');
            }
            setModalVisible(false);
            form.resetFields();
            fetchData();
        } catch (error) {
            message.error('Erro ao salvar');
        }
    };

    return (
        <div>
            <p style={{ color: '#666', marginBottom: 16 }}>CRUD de equipamentos vinculados a sites.</p>
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setModalVisible(true);
                        setEditingEquipment(null);
                        form.resetFields();
                    }}
                    style={{ backgroundColor: '#FB8500', borderColor: '#FB8500' }}
                >
                    Novo Equipamento
                </Button>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                            <th style={{ padding: 12, textAlign: 'left' }}>Nome</th>
                            <th style={{ padding: 12, textAlign: 'left' }}>Tipo</th>
                            <th style={{ padding: 12, textAlign: 'left' }}>Status</th>
                            <th style={{ padding: 12, textAlign: 'left' }}>Capacidade</th>
                        </tr>
                    </thead>
                    <tbody>
                        {equipments.map((eq) => (
                            <tr key={eq.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: 12 }}>{eq.name}</td>
                                <td style={{ padding: 12 }}>{eq.type}</td>
                                <td style={{ padding: 12 }}>{eq.status}</td>
                                <td style={{ padding: 12 }}>{eq.capacity} kW</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal
                title={editingEquipment ? 'Editar Equipamento' : 'Novo Equipamento'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                okText="Salvar"
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="name" label="Nome" rules={[{ required: true }]}>
                        <Input placeholder="Nome do equipamento" />
                    </Form.Item>
                    <Form.Item name="type" label="Tipo" rules={[{ required: true }]}>
                        <Select placeholder="Selecione">
                            <Select.Option value="SOLAR">Solar</Select.Option>
                            <Select.Option value="BATERIA">Bateria</Select.Option>
                            <Select.Option value="GRID">Grid</Select.Option>
                            <Select.Option value="ESTACAO_RECARGA">Estação de Recarga</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="siteId" label="Site" rules={[{ required: true }]}>
                        <Select placeholder="Selecione o site">
                            {sites.map((s) => (
                                <Select.Option key={s.id} value={s.id}>
                                    {s.name || s.nome}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="capacity" label="Capacidade (kW)">
                        <Input type="number" step={0.1} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CadastrosPage;
